import React, { useCallback, useState } from "react";
import useApiEp from "../../hooks/useApiEp";
import { API_ENDPOINT } from "../../utils/constants";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  exportToExcel,
  getErrorResponse,
  getHeader,
  roundToMaxDecimals,
  toastError,
} from "../../utils/util";
import { Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import CustomCircularP from "../../widgets/CustomCircularP";
import { toast } from "react-toastify";
import axios from "axios";
import SimpleYesNoDialog from "../../widgets/SimpleYesNoDialog";
import PageTitle from "../../widgets/PageTitle";
import SearchIcon from "@mui/icons-material/Search";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { debounce } from "lodash";
import DataTable from "../../widgets/DataTable";
import TitleActionsContainer from "../../widgets/TitleActionsContainer";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import { LoadingButton } from "@mui/lab";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
const AccountsList = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [showDelete, setShowDelete] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [toDelete, setToDelete] = useState("");
  const [partner, setPartner] = useState("");
  const [name, setName] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [sq, setSq] = useState("");
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const debouncedSearch = useCallback(
    debounce((value) => {
      setSq(value);
    }, 500),
    []
  );
  const handleSearchChange = (value) => {
    setName(value);
    debouncedSearch(value);
  };
  const { data, isLoading, isError, error, refetch } = useApiEp(
    `/root/get-accounts?page=${page}&limit=${limit}&name=${sq}&partner=${partner}`
  );
  const handleAction = useCallback((action, id) => {
    switch (action) {
      case "view":
        window.open(`/home/${id}/account-details`, "_blank"); // Open in new tab
        break;
      case "edit":
        window.open(`/home/accounts/${id}/edit-account`, "_blank"); // Open in new tab
        break;
      case "delete":
        setToDelete(id);
        setShowDelete(true);
        break;
      default:
        console.warn("unknown action", id);
    }
  }, []);

  if (isError) {
    return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  }

  return (
    <Container sx={{ mt: 3 }}>
      <TitleActionsContainer>
        <PageTitle>Accounts List ({data?.totalItems || 0})</PageTitle>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{
            width: { xs: "100%", sm: "auto" },
            mt: { xs: 1, sm: 0 },
          }}
        >
          <TextField
            size="small"
            value={name}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search Accounts"
            InputProps={{ startAdornment: <SearchIcon fontSize="small" /> }}
          />
          <FormControl size="small" sx={{ width: 120 }} fullWidth>
            <InputLabel>Filter</InputLabel>
            <Select
              value={partner}
              label="Filter"
              onChange={(e) => setPartner(e.target.value)}
            >
              <MenuItem value={""}>
                <i>All</i>
              </MenuItem>
              <MenuItem value={"haptik"}>Haptik</MenuItem>
              <MenuItem value={"aisensy"}>Aisensy</MenuItem>
              <MenuItem value={"rcs"}>RCS Clients</MenuItem>
              <MenuItem value={"rcs2"}>RCS Clients 2</MenuItem>
              <MenuItem value={"primary"}>Primary Accounts</MenuItem>
            </Select>
          </FormControl>
          <Button
            onClick={() => {
              navigate(`/home/add-user-manually`);
            }}
            variant="outlined"
            startIcon={<AddOutlinedIcon />}
          >
            Add User
          </Button>

          <Button
            onClick={() => {
              navigate(`/home/add-account`);
            }}
            variant="outlined"
            startIcon={<AddOutlinedIcon />}
          >
            Add Account
          </Button>
          <LoadingButton
            loading={exporting}
            onClick={async () => {
              try {
                setExporting(true);
                const response = await axios.get(
                  `${API_ENDPOINT}/root/get-all-accounts`,
                  getHeader()
                );
                exportToExcel(response.data, "accounts_list.xlsx");
              } catch (error) {
                toastError(error);
              } finally {
                setExporting(false);
              }
            }}
            variant="contained"
            startIcon={<ImportExportIcon />}
          >
            Export All
          </LoadingButton>
        </Stack>
      </TitleActionsContainer>
      <Box
        display="flex"
        flexDirection={isSmallScreen ? "column" : "row"}
        justifyContent="center"
        alignItems="center"
        mb={1}
        gap={2}
        sx={{ flexWrap: "wrap", width: "100%" }}
      >
        <Button
          variant="outlined"
          fullWidth={isSmallScreen}
          sx={{ mb: isSmallScreen ? 1 : 0 }}
          onClick={() => navigate("/home/subscription-plan-stats")}
          startIcon={<MilitaryTechIcon />}
        >
          Subscription Plan Stats
        </Button>

        <Button
          variant="outlined"
          fullWidth={isSmallScreen}
          onClick={() => navigate("/home/manage-account-groups")}
          startIcon={<GroupWorkIcon />}
        >
          Manage Account Groups
        </Button>
        <Button
          variant="outlined"
          fullWidth={isSmallScreen}
          onClick={() => navigate("/home/manage-reseller")}
          startIcon={<Users size={18} />} // 👈 Lucide icon here
        >
          Manage Reseller
        </Button>
      </Box>
      <CustomCircularP show={deleting || isLoading} />
      <SimpleYesNoDialog
        show={showDelete}
        setShow={setShowDelete}
        title={"Please confirm!"}
        message={"Are you sure you want to delete this item?"}
        onYes={async () => {
          try {
            setDeleting(true);
            setShowDelete(false);
            const response = await axios.delete(
              `${API_ENDPOINT}/root/delete-account/${toDelete}`,
              getHeader()
            );
            refetch();
            toast.success(response.data.message);
          } catch (error) {
            toastError(error);
          } finally {
            setDeleting(false);
          }
        }}
      />

      <DataTable
        sx={{ mb: 1 }}
        columns={[
          {
            id: "serial",
            label: "S.No.",
            render: (_v, _row, idx) => (page - 1) * limit + idx + 1,
          },
          { id: "customerId", label: "CustomerID" },
          {
            id: "subscriptionPlan",
            label: "Waba Plan",
            render: (v) =>
              v ? ( // Check for explicit 0 or truthy value
                <Chip label={v} variant="outlined" size="small" color="info" />
              ) : (
                "NA"
              ),
          },
          {
            id: "expiryDate",
            label: "Expiry Date",
            render: (v) =>
              v
                ? new Date(v).toLocaleString("en-IN", {
                    dateStyle: "medium",
                  })
                : "NA",
          },
          {
            id: "wallet",
            label: "WABA Blnc",
            render: (v) =>
              v === 0 || v ? ( // Check for explicit 0 or truthy value
                <Chip
                  label={`${roundToMaxDecimals(v, 2)}`}
                  variant="outlined"
                  size="small"
                  color="info"
                />
              ) : (
                "NA"
              ),
          },
          {
            id: "voiceCallWallet",
            label: "Voice Blnc",
            render: (v) =>
              v === 0 || v ? ( // Check for explicit 0 or truthy value
                <Chip
                  label={`${roundToMaxDecimals(v, 2)}`}
                  variant="outlined"
                  size="small"
                  color="info"
                />
              ) : (
                "NA"
              ),
          },
          {
            id: "rcsWalletRichMedia",
            label: "RCS Blnc",
            render: (v) =>
              v === 0 || v ? ( // Check for explicit 0 or truthy value
                <Chip
                  label={`${roundToMaxDecimals(v, 2)}`}
                  variant="outlined"
                  size="small"
                  color="info"
                />
              ) : (
                "NA"
              ),
          },
          {
            id: "createdOn",
            label: "Created On",
            render: (v) =>
              new Date(v).toLocaleString("en-IN", {
                dateStyle: "medium",
              }),
          },
        ]}
        actions={["view", "edit", "delete"]}
        rows={data?.data ?? []}
        page={page}
        itemsPerPage={limit}
        totalPages={data?.totalPages ?? 1}
        onPageChange={setPage}
        onAction={handleAction}
        itemsPerPageOptions={[20, 30, 50, 100]}
        onItemsPerPageChange={(lm) => {
          setLimit(lm);
        }}
        paginationAlign="end"
      />
    </Container>
  );
};

export default AccountsList;
